import React, { useState } from 'react';
import { Switch, Route, useParams, Redirect, useLocation, matchPath } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { CompanyDetailGeneral } from '..';
import CompanyDetailServiceContacts from '../serviceContacts/CompanyDetailServiceContacts';
import CompanyDetailProcessesTypeList from './CompanyDetailProcessesTypeList';
import ProcessDetail from '../processes/ProcessDetail';
import CompanyDetailProcessesInstanceList from './CompanyDetailProcessesInstanceList';
import { GetCompanyByCompanyCode } from '../../../utils/statApi/CompaniesApi';
import ICompany from '../../../utils/types/ICompany';
import CompanyHeader from '../header/CompanyHeader';
import AnnualFilingRequirements from '../annualFilingRequirements/AnnualFilingRequirements';
import FiscalYearList from '../fiscalYears/FiscalYearList';
import FiscalYearDetail from '../fiscalYears/FiscalYearDetail';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import ProcessConfigListPage from '../processConfigs/ProcessConfigListPage';
import CompanyDocumentsPage from '../documents/CompanyDocumentsPage';
import ProcessConfigDetail from '../processConfigs/ProcessConfigDetail';
import { ICompanyCodeParams } from '../../common/ParamTypes';
import DividendsReview from '../fiscalYears/dividendsReview/DividendsReview';
import useLocalStorage, { LocalStorageKeys } from '../../../utils/hooks/UseLocalStorage';
import { IRecentCompaniesCardItem } from '../../../utils/types/IRecentCompaniesCardItem';
import { CompanyProvider } from '../CompanyContext';
import AgmReview from '../fiscalYears/AgmReview';
import Page from '../../common/page/Page';

const ROUTE_MAP = [
  {
    pattern: '/companies/:companyCode/service-contacts',
    tabId: 'tab-service-contacts',
    panelId: 'panel-service-contacts',
  },
  {
    pattern: '/companies/:companyCode/documents',
    tabId: 'tab-documents',
    panelId: 'panel-documents',
  },
  {
    pattern: '/companies/:companyCode/fiscal-years',
    tabId: 'tab-fiscal-years',
    panelId: 'panel-fiscal-years',
  },
  {
    pattern: '/companies/:companyCode/dividends-review',
    tabId: 'tab-fiscal-years',
    panelId: 'panel-fiscal-years',
  },
  {
    pattern: '/companies/:companyCode/agm-review',
    tabId: 'tab-fiscal-years',
    panelId: 'panel-fiscal-years',
  },
  {
    pattern: '/companies/:companyCode/processes',
    tabId: 'tab-processes',
    panelId: 'panel-processes',
  },
  {
    pattern: '/companies/:companyCode/annual-filing-requirements',
    tabId: 'tab-annual-filing-requirements',
    panelId: 'panel-annual-filing-requirements',
  },
];

const DEFAULT_PANEL = {
  tabId: 'tab-company-information',
  panelId: 'panel-company-information',
};

const getPanelMetadata = (pathname: string) => {
  const matchedRoute = ROUTE_MAP.find((route) =>
    matchPath(pathname, { path: route.pattern, exact: false }),
  );

  return matchedRoute || DEFAULT_PANEL;
};

const CompanyDetail: React.FunctionComponent = () => {
  const { companyCode } = useParams<ICompanyCodeParams>();
  const location = useLocation();
  const [company, setCompany] = useState<ICompany>({} as ICompany);
  const [recentCompanies, setRecentCompanies] = useLocalStorage(
    LocalStorageKeys.recentCompanies,
    [] as IRecentCompaniesCardItem[],
  );
  const { data, loading, error } = useQuery(GetCompanyByCompanyCode, {
    variables: { companyCode },
    fetchPolicy: 'cache-and-network',
    onCompleted: (freshData) => {
      setRecentCompanies([
        { ...freshData?.company },
        ...recentCompanies
          .filter((comp) => comp.companyCode !== freshData.company.companyCode)
          .slice(0, 7),
      ]);
      setCompany(freshData?.company);
    },
  });

  if (error && error.message.indexOf('The company was not found with that id') !== -1) {
    return <Redirect to="/companies?NotFound=true" />;
  }

  const companyData = data?.company || {};
  const { panelId, tabId } = getPanelMetadata(location.pathname);

  return (
    <CompanyProvider value={{ company, setCompany }}>
      <CompanyHeader company={company} />
      <LoadingErrorMessage loading={loading} error={error} />
      <div role="tabpanel" id={panelId} aria-labelledby={tabId} tabIndex={0}>
        <Switch>
          <Route
            path="/companies/:companyCode"
            exact
            render={(props): JSX.Element => (
              <CompanyDetailGeneral {...props} company={companyData} />
            )}
          />
          <Page
            path="/companies/:companyCode/company-information"
            exact
            render={(props): JSX.Element => (
              <CompanyDetailGeneral {...props} company={companyData} />
            )}
            title={`${companyCode} | Company Info`}
          />
          <Page
            path="/companies/:companyCode/annual-filing-requirements"
            exact
            component={AnnualFilingRequirements}
            requestedPermission="AnnualFilingRequirements:View"
            title={`${companyCode} | Annual Filing Requirements`}
          />
          <Page
            path="/companies/:companyCode/processes"
            exact
            component={CompanyDetailProcessesTypeList}
            requestedPermission="ProcessesList:View"
            title={`${companyCode} | Process Types`}
          />
          <Page
            path="/companies/:companyCode/processes/configs"
            exact
            component={ProcessConfigListPage}
            requestedPermission="ProcessConfiguration:List"
            title={`${companyCode} | Process Configs`}
          />
          <Page
            path="/companies/:companyCode/processes/configs/:processConfigId"
            exact
            component={ProcessConfigDetail}
            requestedPermission="ProcessConfiguration:View"
            title={`${companyCode} | Process Config Detail`}
          />
          <Page
            path="/companies/:companyCode/processes/:processTypeUri"
            exact
            component={CompanyDetailProcessesInstanceList}
            title={`${companyCode} | Processes`}
          />
          <Page
            path="/companies/:companyCode/processes/:processTypeUri/:processId"
            exact
            component={ProcessDetail}
            title={`${companyCode} | Process Detail`}
          />
          <Page
            path="/companies/:companyCode/service-contacts"
            exact
            component={CompanyDetailServiceContacts}
            title={`${companyCode} | Service Contacts`}
          />
          <Page
            path="/companies/:companyCode/fiscal-years"
            exact
            component={FiscalYearList}
            requestedPermission="FiscalYearSummaryList:View"
            title={`${companyCode} | Fiscal Years`}
          />
          <Page
            path="/companies/:companyCode/fiscal-years/:fiscalYearId"
            exact
            component={FiscalYearDetail}
            title={`${companyCode} | Fiscal Year Detail`}
          />
          <Page
            path="/companies/:companyCode/dividends-review/:dividendReviewId"
            exact
            component={DividendsReview}
            title={`${companyCode} | Dividends Review`}
          />
          <Page
            path="/companies/:companyCode/documents"
            exact
            component={CompanyDocumentsPage}
            title={`${companyCode} | Documents`}
          />
          <Page
            path="/companies/:companyCode/agm-review/:agmReviewId"
            exact
            component={AgmReview}
            title={`${companyCode} | AGM Review`}
          />
        </Switch>
      </div>
    </CompanyProvider>
  );
};

export default CompanyDetail;
